/**
 * OpenNebula SSH key pairs + security groups.
 *
 * Endpoints (v2 s10003): /keys (GET/POST), /keys/:id (DELETE);
 * /secgroups (GET/POST), /secgroups/:id (GET/PATCH/DELETE).
 */
import { useMutation, useQuery, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { serviceApi, onPath, onKeys, unwrap } from './_shared';

// ---- SSH keys ------------------------------------------------------------

export interface SshKey {
  id: string;
  name?: string;
  fingerprint?: string;
  public_key?: string;
}

/** Create payload — matches @console `POST /keys`. `type` selects auto-generate
 *  vs import; `content` is the PEM public key (required for import, omitted for
 *  generate, in which case the server generates the pair). */
export interface SshKeyCreatePayload {
  name: string;
  type: 'generate' | 'import';
  /** PEM public key — required for `import`, empty/omitted for `generate`. */
  content?: string;
}

/** The created key. In `generate` mode `content` carries the PRIVATE key (the
 *  only time it is ever returned — download then discard). */
export interface SshKeyCreated {
  id?: string;
  name?: string;
  content?: string;
  fingerprint?: string;
}

export function useSshKeys(serviceId: string | undefined) {
  return useQuery({
    queryKey: onKeys.sshKeys(serviceId ?? ''),
    enabled: !!serviceId,
    retry: false,
    queryFn: async () => {
      const body = unwrap<{ keys?: SshKey[] } | SshKey[]>(await serviceApi.get(onPath(serviceId!, 'keys')));
      return Array.isArray(body) ? body : body.keys ?? [];
    },
  });
}

export function useCreateSshKey(
  serviceId: string,
  options?: UseMutationOptions<SshKeyCreated, unknown, SshKeyCreatePayload>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: SshKeyCreatePayload): Promise<SshKeyCreated> => {
      const res = await serviceApi.post(onPath(serviceId, 'keys'), payload);
      // Envelope: `{ data: { key: {...} } }`; surface the key so `generate` mode
      // callers can read the returned private `content` to download it.
      return unwrap<{ key?: SshKeyCreated }>(res).key ?? {};
    },
    ...options,
    onSuccess: (...a) => {
      qc.invalidateQueries({ queryKey: onKeys.sshKeys(serviceId) });
      options?.onSuccess?.(...a);
    },
  });
}

export function useDeleteSshKey(
  serviceId: string,
  options?: UseMutationOptions<unknown, unknown, string>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (keyId: string) =>
      (await serviceApi.delete(onPath(serviceId, `keys/${keyId}`))).data,
    ...options,
    onSuccess: (...a) => {
      qc.invalidateQueries({ queryKey: onKeys.sshKeys(serviceId) });
      options?.onSuccess?.(...a);
    },
  });
}

// ---- Security groups -----------------------------------------------------

export type RuleType = 'INBOUND' | 'OUTBOUND';
export type RuleProtocol = 'TCP' | 'UDP' | 'ICMP' | 'ICMPv6' | 'IPSEC' | 'ALL';

/** Protocols that carry no port range (port editor disabled). */
export const PORTLESS_PROTOCOLS: RuleProtocol[] = ['ICMP', 'ICMPv6', 'IPSEC', 'ALL'];

export interface SecgroupRule {
  rule_type: RuleType;
  protocol: RuleProtocol;
  /** Friendly preset shown in legacy (e.g. "SSH", "ALL TCP", "HTTPS"). */
  ref_type?: string;
  /** Port or range ("22" or "1:65535"); empty for ICMP/ICMPv6/IPSEC/ALL. */
  range?: string;
  /** Source/dest CIDR; empty = any. */
  ip?: string;
  /**
   * Address count of the network range. OpenNebula stores a range as base
   * `ip` + `size` (not CIDR), so a /24 comes back as `ip:"x.x.x.0", size:"256"`.
   * Read path collapses a power-of-two block into `ip`'s CIDR suffix and clears
   * this; only non-CIDR-aligned ranges keep a bare `ip` + `size`.
   */
  size?: string;
  icmp_type?: string;
}

/** Create/update payload — legacy splits rules into inbound/outbound arrays. */
export interface SecgroupWritePayload {
  name: string;
  description?: string;
  inbound: SecgroupRule[];
  outbound: SecgroupRule[];
}

/** Instances a security group is attached to (blocks deletion when non-empty). */
export interface SecgroupAttachment {
  id?: string;
  instance_id?: string;
  name?: string;
}

export interface Secgroup {
  id: string;
  uid?: string;
  name?: string;
  description?: string;
  attached?: SecgroupAttachment[];
  rules?: SecgroupRule[];
  /** Raw API container: `template.rule[]` + description/timestamps. */
  template?: { rule?: SecgroupRule[]; description?: string; created_at?: string; updated_at?: string };
  created_at?: string;
}

/** Rules of a secgroup, tolerating both the flat and `template.rule` shapes. */
export function secgroupRules(sg?: Secgroup): SecgroupRule[] {
  return sg?.template?.rule ?? sg?.rules ?? [];
}

/**
 * Render a rule's network as CIDR. The API returns a range as a base `ip` plus
 * a `size` (address count), so a /24 arrives as `{ ip:"x.x.x.0", size:"256" }`
 * — which would show as a single host. Fold a power-of-two block into
 * `ip/<prefix>`; single hosts (`size` ≤ 1) and non-CIDR-aligned ranges return
 * the bare `ip` unchanged. Safe on already-CIDR or empty input.
 */
export function ruleCidr(ip?: string, size?: string): string {
  if (!ip || ip.includes('/')) return ip ?? '';
  const n = Number(size);
  if (!Number.isFinite(n) || n <= 1) return ip;
  const prefix = 32 - Math.log2(n);
  return Number.isInteger(prefix) && prefix >= 0 && prefix <= 32 ? `${ip}/${prefix}` : ip;
}

/**
 * Size field to send on save: a CIDR `ip` needs none (the backend re-derives
 * ip+size from the suffix); a bare `ip` keeps its `size` so a non-CIDR-aligned
 * range survives the round-trip. Inverse of `ruleCidr`'s read-path fold.
 */
export function ruleSizeForPayload(ip: string, size?: string): { size?: string } {
  return size && !ip.includes('/') ? { size } : {};
}

/**
 * Fold a rule's `ip` + `size` into a CIDR for display/edit. When the block
 * collapses cleanly (or is a single host / already CIDR), `size` is dropped
 * since the backend re-derives ip+size from the CIDR on save. A non-CIDR-aligned
 * multi-address range keeps its bare `ip` + `size` verbatim.
 */
function ruleNetworkToCidr(r: SecgroupRule): SecgroupRule {
  const cidr = ruleCidr(r.ip, r.size);
  const n = Number(r.size);
  const keepSize = !!r.ip && !r.ip.includes('/') && cidr === r.ip && Number.isFinite(n) && n > 1;
  return keepSize ? r : { ...r, ip: cidr, size: undefined };
}

/** Normalize the API row so views can read `.rules`/`.description` directly. */
function normalizeSecgroup(sg: Secgroup): Secgroup {
  const rules = secgroupRules(sg).map(ruleNetworkToCidr);
  return {
    ...sg,
    description: sg.description ?? sg.template?.description,
    created_at: sg.created_at ?? sg.template?.created_at,
    rules,
    // Keep `template.rule` in sync so glance views reading it stay consistent.
    template: sg.template ? { ...sg.template, rule: rules } : sg.template,
  };
}

export function useSecgroups(serviceId: string | undefined) {
  return useQuery({
    queryKey: onKeys.secgroups(serviceId ?? ''),
    enabled: !!serviceId,
    retry: false,
    queryFn: async () => {
      const body = unwrap<{ secgroups?: Secgroup[] } | Secgroup[]>(
        await serviceApi.get(onPath(serviceId!, 'secgroups')),
      );
      const list = Array.isArray(body) ? body : body.secgroups ?? [];
      return list.map(normalizeSecgroup);
    },
  });
}

export function useSecgroup(serviceId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: onKeys.secgroup(serviceId ?? '', id ?? ''),
    enabled: !!serviceId && !!id,
    retry: false,
    queryFn: async () => {
      const body = unwrap<{ secgroup?: Secgroup } | Secgroup>(
        await serviceApi.get(onPath(serviceId!, `secgroups/${id}`)),
      );
      const sg = (body && typeof body === 'object' && 'secgroup' in body ? body.secgroup : body) as Secgroup;
      return normalizeSecgroup(sg);
    },
  });
}

export function useCreateSecgroup(
  serviceId: string,
  options?: UseMutationOptions<unknown, unknown, SecgroupWritePayload>,
) {
  const qc = useQueryClient();
  return useMutation({
    // Rails `secgroup_params` does `params.require(:secgroup)`, so the body must
    // be wrapped under a `secgroup` key (a flat body 500s "param is missing: secgroup").
    mutationFn: async (payload: SecgroupWritePayload) =>
      (await serviceApi.post(onPath(serviceId, 'secgroups'), { secgroup: payload })).data,
    ...options,
    onSuccess: (...a) => {
      qc.invalidateQueries({ queryKey: onKeys.secgroups(serviceId) });
      options?.onSuccess?.(...a);
    },
  });
}

export function useUpdateSecgroup(
  serviceId: string,
  secgroupId: string,
  options?: UseMutationOptions<unknown, unknown, SecgroupWritePayload>,
) {
  const qc = useQueryClient();
  return useMutation({
    // Body must be wrapped under `secgroup` — Rails `params.require(:secgroup)`.
    mutationFn: async (payload: SecgroupWritePayload) =>
      (await serviceApi.patch(onPath(serviceId, `secgroups/${secgroupId}`), { secgroup: payload })).data,
    ...options,
    onSuccess: (...a) => {
      qc.invalidateQueries({ queryKey: onKeys.secgroups(serviceId) });
      qc.invalidateQueries({ queryKey: onKeys.secgroup(serviceId, secgroupId) });
      options?.onSuccess?.(...a);
    },
  });
}

export function useDeleteSecgroup(
  serviceId: string,
  options?: UseMutationOptions<unknown, unknown, string>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (await serviceApi.delete(onPath(serviceId, `secgroups/${id}`))).data,
    ...options,
    onSuccess: (...a) => {
      qc.invalidateQueries({ queryKey: onKeys.secgroups(serviceId) });
      options?.onSuccess?.(...a);
    },
  });
}

// ---- Attach/detach a security group to/from a NIC ------------------------
// OpenNebula scopes security groups PER NIC, not per instance. The Rails
// `attachsg`/`detachsg` actions require `policy_id` (the secgroup id) AND
// `network_id` (the target NIC_ID) — a flat `{secgroup_id}` body silently fails.

/** Arguments for a per-NIC security-group attach/detach. */
export interface NicSecgroupArgs {
  /** NIC_ID the secgroup applies to (instance.metadata.nics[].nic_id). */
  networkId: string | number;
  /** Security group id. */
  policyId: string;
}

export function useAttachSecgroupToNic(
  serviceId: string,
  instanceId: string,
  options?: UseMutationOptions<unknown, unknown, NicSecgroupArgs>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ networkId, policyId }: NicSecgroupArgs) =>
      (await serviceApi.post(onPath(serviceId, `instances/${instanceId}/secgroups/attachsg`), { network_id: networkId, policy_id: policyId })).data,
    ...options,
    onSuccess: (...a) => {
      qc.invalidateQueries({ queryKey: onKeys.instance(serviceId, instanceId) });
      options?.onSuccess?.(...a);
    },
  });
}

export function useDetachSecgroupFromNic(
  serviceId: string,
  instanceId: string,
  options?: UseMutationOptions<unknown, unknown, NicSecgroupArgs>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ networkId, policyId }: NicSecgroupArgs) =>
      (await serviceApi.post(onPath(serviceId, `instances/${instanceId}/secgroups/detachsg`), { network_id: networkId, policy_id: policyId })).data,
    ...options,
    onSuccess: (...a) => {
      qc.invalidateQueries({ queryKey: onKeys.instance(serviceId, instanceId) });
      options?.onSuccess?.(...a);
    },
  });
}
